export interface FilterValue {
    name: string;
    value: string | number;
}

export interface BaseFilterOption extends FilterValue {
    id?: string;
    label: string;
    url: string;
    checked: boolean;
    disabled: boolean;
    is_default?: boolean;
    is_suggested?: boolean;
    children?: BaseFilterOption[];
    /**
     * Analytics event's label prefix.
     * It's needed for distinguishing the same filtering events, but happened in different components.
     *
     * @see /media/web/react/components/feeds-filters/hooks/use-filter-click-handler.ts getPrefixedAnalyticsLabel
     */
    event_prefix?: string;
    /**
     * Is the option marked as relevant for the Onboarding feature?
     *
     * @see /media/web/react/components/feed-onboarding/feed-onboarding-modal/feed-onboarding-modal-brand-tab/
     */
    is_onboarding?: boolean;
    // Logo used for Onboarding Brands list.
    logo?: string;
    top_level_category?: string;
}

export interface InputFilterOption extends BaseFilterOption {
    type: "checkbox" | "radio";
}

export interface OptionFilterOption extends BaseFilterOption {
    type: "option";
}

export interface CategoryFilterOption extends BaseFilterOption {
    type: "category";
    children: (InputFilterOption | CategoryFilterOption)[];
}

export type UniversalFilterOption = InputFilterOption | OptionFilterOption | CategoryFilterOption;

// ------

interface BaseFilterOptionGroup {
    id: string;
    title: null | string;
}

interface NormalFilterOptionGroup extends BaseFilterOptionGroup {
    type: null | "scroll_area";
    options: (InputFilterOption | CategoryFilterOption)[];
}

export interface SelectFilterOptionGroup extends BaseFilterOptionGroup {
    type: "select";
    options: OptionFilterOption[];
}

export interface FilteredScrollAreaFilterOptionGroup extends BaseFilterOptionGroup {
    search_placeholder: string;
    type: "filtered_scroll_area";
    options: InputFilterOption[];
    lazy_loaded: boolean;
}

export type UniversalFilterOptionGroup =
    | NormalFilterOptionGroup
    | SelectFilterOptionGroup
    | FilteredScrollAreaFilterOptionGroup;

// ------

interface BaseFilterSection {
    id: string;
    title: string;
    all_option_names: string;
    checked?: boolean;
    render_condition: "main" | "more" | null;
}

export interface NormalFilterSection extends BaseFilterSection {
    type: null | "accordion";
    option_groups: [NormalFilterOptionGroup | FilteredScrollAreaFilterOptionGroup];
    active_filter_count: number;
}

export interface SizesFilterSection extends BaseFilterSection {
    type: "sizes";
    option_groups: [NormalFilterOptionGroup];
    active_filter_count: number;
}

export interface ColorsFilterSection extends BaseFilterSection {
    type: "colors";
    option_groups: [NormalFilterOptionGroup];
    active_filter_count: number;
}

export interface PairOfSelectsFilterSection extends BaseFilterSection {
    type: "pair_of_selects";
    option_groups: [SelectFilterOptionGroup, SelectFilterOptionGroup];
}

export type UniversalFilterSection =
    | NormalFilterSection
    | PairOfSelectsFilterSection
    | ColorsFilterSection
    | SizesFilterSection;

export interface UniversalFiltersData {
    // The base URL for what we landed on
    feed_url: string;
    // The feeds query string for this current set of filters
    query_string: string;
    custom_query_string: string;
    canonical_url: string;
    // Available filters
    filter_section_list: UniversalFilterSection[];
    // Prefilters the user can't disable
    hidden_inputs: FilterValue[];
}

export function isFilterSection(item: Object): item is UniversalFilterSection {
    return item && item.hasOwnProperty("id") && item.hasOwnProperty("all_option_names");
}

export function isCategorySection(item: Object): item is CategoryFilterOption {
    return item && item.hasOwnProperty("id") && item.hasOwnProperty("value");
}
